import { Stack, Typography, useTheme } from "@mui/material";
import type { Control } from "react-hook-form";
import {
	ClientPortfolioDataCommodityServiceEnum,
	ClientPortfolioDomainEnum,
	ClientPortfolioPaymentFrequencyEnum,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import type {
	ServicesFormInput,
	ServicesFormOutput,
} from "web_myplann/Portfolio/Forms/ServicesForm/servicesFormSchema";
import { useProducerProductOptions } from "web_myplann/Portfolio/Helpers/useProducerProductOptions";
import { FlatRowFormCombobox } from "web_myplann/Shared/Components/FlatRow/FlatRowFormCombobox";
import { FlatRowFormDatePicker } from "web_myplann/Shared/Components/FlatRow/FlatRowFormDatePicker";
import { FlatRowFormInput } from "web_myplann/Shared/Components/FlatRow/FlatRowFormInput";
import { FlatRowFormSelect } from "web_myplann/Shared/Components/FlatRow/FlatRowFormSelect";
import { useEnumSelectOptions } from "web_myplann/Shared/Hooks/useEnumSelectOptions";
import { DateFormat } from "web_myplann/Shared/Utils/dateFormats";

type ServicesFormFieldsProps = {
	control: Control<ServicesFormInput, unknown, ServicesFormOutput>;
};

export const ServicesFormFields = ({ control }: ServicesFormFieldsProps) => {
	const theme = useTheme();
	const { t } = useResource();

	const { producerOptions, productOptions, isLoadingProducts } =
		useProducerProductOptions(control, ClientPortfolioDomainEnum.Services);

	const commodityServiceOptions = useEnumSelectOptions(
		ClientPortfolioDataCommodityServiceEnum,
		Resources.PortfolioDetail.Services.CommodityService,
		[ClientPortfolioDataCommodityServiceEnum.None],
	);

	const paymentFrequencyOptions = useEnumSelectOptions(
		ClientPortfolioPaymentFrequencyEnum,
		Resources.PortfolioDetail.SharedLabels.Frequency,
	);

	return (
		<>
			<Typography
				variant="overline"
				display="block"
				pt={2.4}
				px={theme.spacingTokens.lg}
				pb={theme.spacingTokens.sm}
			>
				{t(Resources.Portfolio.AddServices.Sections.basicInfo)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormInput
					control={control}
					name="name"
					label={t(Resources.Portfolio.AddServices.Fields.name)}
					placeholder={t(Resources.Portfolio.AddServices.Placeholders.fill)}
				/>
				<FlatRowFormCombobox
					control={control}
					name="producer"
					label={t(Resources.Portfolio.AddServices.Fields.producer)}
					placeholder={t(Resources.Portfolio.AddServices.Placeholders.optional)}
					searchPlaceholder={t(
						Resources.Portfolio.AddShared.Placeholders.searchOrEnterProducer,
					)}
					options={producerOptions}
				/>
				<FlatRowFormCombobox
					control={control}
					name="product"
					label={t(Resources.Portfolio.AddServices.Fields.product)}
					placeholder={t(Resources.Portfolio.AddServices.Placeholders.optional)}
					searchPlaceholder={t(
						Resources.Portfolio.AddShared.Placeholders.searchOrEnterProduct,
					)}
					options={productOptions}
					isLoading={isLoadingProducts}
				/>
				<FlatRowFormInput
					control={control}
					name="contractNumber"
					label={t(Resources.Portfolio.AddServices.Fields.contractNumber)}
					placeholder={t(Resources.Portfolio.AddServices.Placeholders.optional)}
					isLast
				/>
			</Stack>

			<Typography
				variant="overline"
				display="block"
				pt={2.4}
				px={theme.spacingTokens.lg}
				pb={theme.spacingTokens.sm}
			>
				{t(Resources.Portfolio.AddServices.Sections.values)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormInput
					control={control}
					name="tariff"
					label={t(Resources.Portfolio.AddServices.Fields.tariff)}
					placeholder={t(Resources.Portfolio.AddServices.Placeholders.optional)}
				/>
				<FlatRowFormSelect
					control={control}
					name="commodityService"
					label={t(Resources.Portfolio.AddServices.Fields.commodityService)}
					options={commodityServiceOptions}
				/>
				<FlatRowFormInput
					control={control}
					name="unitOfMeasurement"
					label={t(Resources.Portfolio.AddServices.Fields.unitOfMeasurement)}
					placeholder={t(Resources.Portfolio.AddServices.Placeholders.optional)}
				/>
				<FlatRowFormInput
					control={control}
					name="pricePerUnit"
					type="float"
					label={t(Resources.Portfolio.AddServices.Fields.pricePerUnit)}
					placeholder={t(Resources.Portfolio.AddServices.Placeholders.optional)}
					suffix="Kč"
				/>
				<FlatRowFormInput
					control={control}
					name="fixedPriceByFrequency"
					type="float"
					label={t(
						Resources.Portfolio.AddServices.Fields.fixedPriceByFrequency,
					)}
					placeholder={t(Resources.Portfolio.AddServices.Placeholders.optional)}
					suffix="Kč"
				/>
				<FlatRowFormSelect
					control={control}
					name="paymentFrequency"
					label={t(Resources.Portfolio.AddServices.Fields.paymentFrequency)}
					options={paymentFrequencyOptions}
				/>
				<FlatRowFormInput
					control={control}
					name="depositAmount"
					type="float"
					label={t(Resources.Portfolio.AddServices.Fields.depositAmount)}
					placeholder={t(Resources.Portfolio.AddServices.Placeholders.optional)}
					suffix="Kč"
					isLast
				/>
			</Stack>

			<Typography
				variant="overline"
				display="block"
				pt={2.4}
				px={theme.spacingTokens.lg}
				pb={theme.spacingTokens.sm}
			>
				{t(Resources.Portfolio.AddServices.Sections.additionalInfo)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormDatePicker
					control={control}
					name="signedAt"
					label={t(Resources.Portfolio.AddServices.Fields.signedAt)}
					views={["year", "month", "day"]}
					displayFormat={DateFormat.Display}
				/>
				<FlatRowFormDatePicker
					control={control}
					name="startAt"
					label={t(Resources.Portfolio.AddServices.Fields.startAt)}
					views={["year", "month", "day"]}
					displayFormat={DateFormat.Display}
				/>
				<FlatRowFormDatePicker
					control={control}
					name="endAt"
					label={t(Resources.Portfolio.AddServices.Fields.endAt)}
					views={["year", "month", "day"]}
					displayFormat={DateFormat.Display}
					isLast
				/>
			</Stack>
		</>
	);
};
